import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Settings, DollarSign, Trophy, Star, Gift } from "lucide-react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SuperAdminSidebar } from "@/components/admin/SuperAdminSidebar";
import { useSidebar } from "@/contexts/SidebarContext";

export default function RewardSettings() {
  const { getContentMargin } = useSidebar();

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className={`flex-1 ${getContentMargin()} p-4 lg:p-8 pt-16 lg:pt-8`}>
          <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
          <Crown className="h-5 w-5 text-yellow-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 space-grotesk">Reward Settings</h1>
          <p className="text-gray-600 space-grotesk">Configure reward programs and incentive structures</p>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Reward Tiers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 space-grotesk">
              <Trophy className="h-5 w-5 text-yellow-600" />
              Reward Tiers
            </CardTitle>
            <CardDescription className="space-grotesk">
              Configure different reward tier levels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Bronze tier</span>
                <Badge variant="secondary" className="space-grotesk">1-5 refs</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Silver tier</span>
                <Badge variant="default" className="space-grotesk">6-15 refs</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Gold tier</span>
                <Badge className="bg-yellow-500 text-white space-grotesk">16+ refs</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bonus Structure */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 space-grotesk">
              <DollarSign className="h-5 w-5 text-green-600" />
              Bonus Structure
            </CardTitle>
            <CardDescription className="space-grotesk">
              Set bonus amounts for different achievements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">First referral</span>
                <Badge variant="secondary" className="space-grotesk">$100</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Successful placement</span>
                <Badge variant="default" className="space-grotesk">$500</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Monthly bonus</span>
                <Badge variant="secondary" className="space-grotesk">$250</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recognition Rewards */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 space-grotesk">
              <Star className="h-5 w-5 text-purple-600" />
              Recognition
            </CardTitle>
            <CardDescription className="space-grotesk">
              Configure recognition and achievement badges
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Public leaderboard</span>
                <Badge variant="default" className="space-grotesk">Enabled</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Achievement badges</span>
                <Badge variant="default" className="space-grotesk">Enabled</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Email announcements</span>
                <Badge variant="secondary" className="space-grotesk">Disabled</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Special Incentives */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 space-grotesk">
              <Gift className="h-5 w-5 text-pink-600" />
              Special Incentives
            </CardTitle>
            <CardDescription className="space-grotesk">
              Configure special reward campaigns and incentives
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Holiday bonuses</span>
                <Badge variant="default" className="space-grotesk">Active</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Team challenges</span>
                <Badge variant="secondary" className="space-grotesk">Inactive</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Referral contests</span>
                <Badge variant="default" className="space-grotesk">Monthly</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payout Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 space-grotesk">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Payout Settings
            </CardTitle>
            <CardDescription className="space-grotesk">
              Configure reward payout methods and schedules
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Payout frequency</span>
                <Badge variant="secondary" className="space-grotesk">Monthly</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Minimum payout</span>
                <Badge variant="secondary" className="space-grotesk">$50</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 space-grotesk">Payment method</span>
                <Badge variant="default" className="space-grotesk">Bank transfer</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Placeholder Notice */}
      <Card className="border-dashed border-2 border-gray-300">
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <Settings className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="text-lg font-semibold text-gray-900 space-grotesk">Reward Settings Configuration</h3>
            <p className="text-gray-600 space-grotesk max-w-md mx-auto">
              This is a placeholder page for reward settings. Detailed configuration options and forms will be implemented here.
            </p>
          </div>
        </CardContent>
      </Card>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
